import countries from 'i18n-iso-countries';
import fr from 'i18n-iso-countries/langs/fr.json';
import {
  getExampleNumber,
  type CountryCode,
  parsePhoneNumberFromString,
  getCountries,
} from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';

countries.registerLocale(fr);

export const getFlagEmoji = (countryCode: string) => {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

export const libIsoCountries = countries.getNames('fr', { select: 'official' });
export const libPhoneNumberCountries = getCountries();

export const countryOptionsDataMap = Object.fromEntries(
  libPhoneNumberCountries
    .map(code => {
      const name = libIsoCountries[code];
      const emoji = getFlagEmoji(code);
      if (!name || !emoji) return null;
      return [
        code,
        {
          code,
          name,
          emoji,
        },
      ] as [
        CountryCode,
        {
          code: CountryCode;
          name: string;
          emoji: string;
        },
      ];
    })
    .filter(o => !!o)
);

export const countryOptionsData = Object.values(countryOptionsDataMap);

export type Country = (typeof countryOptionsData)[number];

export const getFormat = (countryCode: CountryCode) => {
  const example = getExampleNumber(countryCode, examples)!.formatNational();
  const mask = example.replace(/\d/g, '0');
  return { example, mask };
};

export const getInitialDataFromValue = (
  value: string | undefined,
  options: {
    initialCountryCode: string;
  }
): {
  country: Country;
  format: ReturnType<typeof getFormat>;
  localValue: string;
} => {
  const defaultValue = {
    country: countryOptionsDataMap[options.initialCountryCode],
    format: getFormat(options.initialCountryCode as CountryCode),
    localValue: '',
  };
  if (!value) return defaultValue;
  const phoneNumber = parsePhoneNumberFromString(value);
  if (!phoneNumber) return defaultValue;
  if (!phoneNumber.country) return defaultValue;
  return {
    country: countryOptionsDataMap[phoneNumber.country],
    localValue: phoneNumber.formatNational(),
    format: getFormat(phoneNumber.country),
  };
};
